<template>
  <v-container fluid class="pa-0">
    <v-card outlined>
      <v-card-text class="pa-4">
        <v-form ref="reportForm">
          <v-row>
            <v-col cols="12" sm="6">
              <v-menu v-model="startDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="report.parameters.startDate" label="Start Date" prepend-icon="mdi-calendar"
                    readonly outlined dense v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="report.parameters.startDate" @input="startDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
            <v-col cols="12" sm="6">
              <v-menu v-model="endDateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field v-model="report.parameters.endDate" label="End Date" prepend-icon="mdi-calendar"
                    readonly outlined dense v-bind="attrs" v-on="on"></v-text-field>
                </template>
                <v-date-picker v-model="report.parameters.endDate" @input="endDateMenu = false"></v-date-picker>
              </v-menu>
            </v-col>
          </v-row>
          <v-row class="mt-4">
            <v-col cols="12" class="text-center">
              <v-btn color="primary" @click="generatePreview" :loading="isGeneratingPreview" large>
                <v-icon left>mdi-eye</v-icon>
                Generate Preview
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <template v-if="groupedData && groupedData.length > 0">
          <v-row class="mt-8">
            <v-col cols="12" md="6">
              <div class="d-flex align-center flex-wrap gap-4 mb-4">
                <strong>Filter by Unit:</strong>
                <v-select
                  v-model="selectedUnit"
                  :items="unitOptions"
                  label="Select Unit"
                  outlined
                  dense
                  clearable
                  prepend-icon="mdi-filter"
                  class="unit-filter"
                  style="max-width: 300px;"
                ></v-select>
              </div>

              <div class="d-flex align-center flex-wrap gap-4">
                <strong>Sort by:</strong>
                <v-btn-toggle v-model="sortMode" mandatory dense>
                  <v-btn small value="reference">Reference</v-btn>
                  <v-btn small value="unit">Unit</v-btn>
                </v-btn-toggle>
              </div>
            </v-col>

            <v-col cols="12" md="6" class="text-md-right d-flex justify-md-end gap-2 flex-wrap">
              <v-btn color="red" dark @click="generatePdf" :loading="generatingPdf">
                <v-icon left>mdi-file-pdf-box</v-icon>
                Export PDF
              </v-btn>
            </v-col>
          </v-row>

          <!-- Printable report content -->
          <div id="print-content" class="pdf-content mt-8">
            <div class="report-header text-center mb-8">
              <img v-if="base64log" :src="logoSrc" style="max-height: 200px; max-width: 200px;" />
              <h1 class="mt-4">{{ report.title }}</h1>

              <!-- Show Landlord when in landlord mode -->
              <p v-if="isLandlordMode">
                <strong>Landlord:</strong> {{ landlordName }}
              </p>

              <!-- Property / Properties line -->
              <p>
                <strong>{{ isLandlordMode ? 'Properties' : 'Property' }}:</strong>
                {{ headerPropertyText }}
              </p>

              <p><strong>Period:</strong> {{ formatDate(report.parameters.startDate) }} to {{ formatDate(report.parameters.endDate) }}</p>
              <hr style="border-top: 1px solid #000; margin: 20px 0;" />
            </div>

            <div 
              v-for="(group, index) in displayedGroupedData" 
              :key="index" 
              class="lease-card mb-12"
            >
              <div class="lease-header grey lighten-4 py-2 text-subtitle-1">
                <div class="d-flex" style="gap: 20px; flex-wrap: wrap;">
                  <div><strong>Ref:</strong> {{ group.reference }}</div>
                  <div><strong>Tenant:</strong> {{ group.tenantName || 'Unknown Tenant' }}</div>
                  <div><strong>Property:</strong> {{ group.property }}</div>
                  <div v-if="group.unit"><strong>Unit:</strong> {{ group.unit }}</div>
                </div>
              </div>
              <hr style="margin: 0;" />
              <div class="pa-0">
                <table class="rent-roll-table">
                  <thead>
                    <tr>
                      <th v-for="col in visibleMonthColumns" :key="col.field">{{ col.header }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in group.items" :key="item.month">
                      <td v-for="col in visibleMonthColumns" :key="col.field">
                        <template v-if="col.field === 'balance'">
                          <span :class="getBalanceClass(item)">
                            {{ formatCurrency(item.balance) }}
                            <span v-if="item.balance < 0" class="ml-1">(Credit)</span>
                          </span>
                        </template>
                        <template v-else-if="chargeColumns.includes(col.field)">
                          <div class="combined-cell">
                            <div>{{ formatCurrency(item[col.field + 'Charged'] || item[col.field] || 0) }}</div>
                            <div style="border-top: 1px solid #ccc; margin: 2px 0;"></div>
                            <div :class="getPaidClass(item[col.field + 'Charged'] || item[col.field] || 0, item[col.field + 'Paid'] || item[col.field + 'ChargedPaid'] || 0)">
                              {{ formatCurrency(item[col.field + 'Paid'] || item[col.field + 'ChargedPaid'] || 0) }}
                            </div>
                          </div>
                        </template>
                        <template v-else-if="col.field === 'totalAllocated'">
                          <span class="success--text font-weight-bold">{{ formatCurrency(item.totalAllocated) }}</span>
                        </template>
                        <template v-else-if="col.field === 'unallocated'">
                          <span :class="item.unallocated > 0 ? 'error--text font-weight-bold' : ''">{{ formatCurrency(item.unallocated) }}</span>
                        </template>
                        <template v-else>
                          {{ col.formatter ? col.formatter(item[col.field]) : item[col.field] }}
                        </template>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div class="report-legend mt-8">
              <div class="text-subtitle-1 font-weight-bold mb-4">Legend</div>
              <div class="d-flex" style="gap: 40px; flex-wrap: wrap;">
                <div class="d-flex align-center">
                  <div class="legend-square success-bg mr-3"></div>
                  <span class="font-weight-bold">Fully paid</span>
                </div>
                <div class="d-flex align-center">
                  <div class="legend-square orange-bg mr-3"></div>
                  <span class="font-weight-bold">Partially paid</span>
                </div>
                <div class="d-flex align-center">
                  <div class="legend-square error-bg mr-3"></div>
                  <span class="font-weight-bold">Not paid</span>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div v-else-if="groupedData && groupedData.length === 0" class="text-center py-8">
          <p class="grey--text text--darken-1">No leases match the selected unit filter.</p>
        </div>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const db = getFirestore();

export default {
  name: "RentRollReport",
  props: {
    property: { type: Object, default: null },
    properties: { type: Array, default: () => [] },
    unit: { type: Object, default: null },
    landlord: { type: Object, default: null }
  },
  data() {
    const today = new Date();
    const sixMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 6, 1);
    const lastDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);

    return {
      propertyName: '',
      landlordName: '',
      report: {
        title: 'Rent Roll Report',
        parameters: {
          startDate: sixMonthsAgo.toISOString().split('T')[0],
          endDate: lastDayOfCurrentMonth.toISOString().split('T')[0],
        },
      },
      startDateMenu: false,
      endDateMenu: false,
      isGeneratingPreview: false,
      generatingPdf: false,
      groupedData: [],
      flatDataForExport: [],
      base64log: "",
      sortMode: 'unit',
      selectedUnit: null,
      unitOptions: [{ text: 'All Units', value: null }],
      chargeColumns: ['rent','water','sewerage','sewerageAv','refuse','admin','parking','insurance','legalExpenses','repairsMaintenance','utilities','otherVendorExpenses','otherCharge'],
      monthColumnDefinitions: [
        { field: 'month', header: 'Month', alwaysVisible: true },
        { field: 'bcForward', header: 'B/C Forward', isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'rent', header: 'Rent', alwaysVisible: true },
        { field: 'water', header: 'Water', alwaysVisible: true },
        { field: 'sewerage', header: 'Sewerage', alwaysVisible: true },
        { field: 'sewerageAv', header: 'Sewerage Av', alwaysVisible: true },
        { field: 'refuse', header: 'Refuse', alwaysVisible: true },
        { field: 'admin', header: 'Admin' },
        { field: 'parking', header: 'Parking' },
        { field: 'insurance', header: 'Insurance' },
        { field: 'legalExpenses', header: 'Legal Expenses' },
        { field: 'repairsMaintenance', header: 'Repairs & Maintenance' },
        { field: 'utilities', header: 'Utilities' },
        { field: 'otherVendorExpenses', header: 'Other Vendor Expenses' },
        { field: 'otherCharge', header: 'Other Charge' },
        { field: 'subTotal', header: 'Sub Total', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'paymentDate', header: 'Payment Date', alwaysVisible: true },
        { field: 'bankReceived', header: 'Bank Received', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'totalAllocated', header: 'Total Allocated', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'unallocated', header: 'Unallocated', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
        { field: 'balance', header: 'Balance', alwaysVisible: true, isTotalable: true, formatter: v => v == null ? '' : 'R ' + Number(v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
      ],
      visibleColumns: {}
    };
  },
  computed: {
    isLandlordMode() {
      return this.properties.length > 1 || (this.properties.length === 1 && this.landlord);
    },
    landlordName() {
      return this.landlord?.name || this.landlord?.displayAs || 'Unknown Landlord';
    },
    headerPropertyText() {
      if (this.isLandlordMode) {
        return `All properties under ${this.landlordName}`;
      }
      return this.propertyName || 'Unknown Property';
    },
    visibleMonthColumns() {
      return this.monthColumnDefinitions.filter(col => col.alwaysVisible || this.visibleColumns[col.field]);
    },
    logoSrc() {
      return this.base64log ? this.base64log : '';
    },
    sortedGroupedData() {
      if (!this.groupedData.length) return [];
      const data = [...this.groupedData];
      if (this.sortMode === 'unit') {
        data.sort((a, b) => {
          const unitA = a.unit ? parseInt(a.unit, 10) : 999999;
          const unitB = b.unit ? parseInt(b.unit, 10) : 999999;
          return unitA - unitB;
        });
      } else {
        data.sort((a, b) => a.reference.localeCompare(b.reference));
      }
      return data;
    },
    displayedGroupedData() {
      if (!this.selectedUnit) return this.sortedGroupedData;
      return this.sortedGroupedData.filter(group => group.unit === this.selectedUnit);
    }
  },
  async created() {
    await this.fetchLogoBase64();
  },
  methods: {
    async fetchLogoBase64() {
      try {
        const logosRef = collection(db, 'Logo');
        const q = query(logosRef, where('id', '==', 2));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          this.base64log = snapshot.docs[0].data().logo2 || '';
        }
      } catch (err) {
        console.error('Logo fetch failed:', err);
      }
    },
    getPaidClass(charged, paid) {
      if (paid >= charged - 0.01) return 'success--text font-weight-bold';
      if (paid > 0) return 'orange--text font-weight-bold';
      return 'error--text font-weight-bold';
    },
    getBalanceClass(item) {
      if (item.balance <= 0) return 'success--text font-weight-bold';
      if (item.bankReceived > 0) return 'orange--text font-weight-bold';
      return 'error--text font-weight-bold';
    },
    updateVisibleColumns() {
      if (!this.groupedData.length) {
        this.visibleColumns = {};
        return;
      }
      const newVisible = {};
      this.monthColumnDefinitions.forEach(col => {
        if (col.alwaysVisible) return;
        let hasNonZero = false;
        if (this.chargeColumns.includes(col.field)) {
          hasNonZero = this.groupedData.some(group =>
            group.items.some(item => {
              const charged = Number(item[col.field + 'Charged'] || item[col.field] || 0);
              const paid = Number(item[col.field + 'Paid'] || item[col.field + 'ChargedPaid'] || 0);
              return charged !== 0 || paid !== 0;
            })
          );
        } else {
          hasNonZero = this.groupedData.some(group =>
            group.items.some(item => Number(item[col.field] || 0) !== 0)
          );
        }
        newVisible[col.field] = hasNonZero;
      });
      this.visibleColumns = newVisible;
    },
    extractUniqueUnits() {
      const units = new Set();
      this.groupedData.forEach(group => {
        if (group.unit) units.add(group.unit);
      });
      this.unitOptions = [
        { text: 'All Units', value: null },
        ...Array.from(units)
          .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
          .map(u => ({ text: `Unit ${u}`, value: u }))
      ];
    },
    async generateRentRollData() {
      try {
        const targetProperties = this.properties.length > 0 
          ? this.properties 
          : this.property ? [this.property] : [];

        if (targetProperties.length === 0) {
          console.warn("No properties selected for Rent Roll");
          this.groupedData = [];
          return;
        }

        // Set names and title
        if (this.isLandlordMode) {
          this.landlordName = this.landlord?.name || this.landlord?.displayAs || 'Multiple Landlords';
          this.propertyName = ''; // Not used in landlord mode
          this.report.title = `${this.landlordName} - Consolidated Rent Roll`;
        } else {
          this.propertyName = targetProperties[0].displayName || 'Unknown Property';
          this.landlordName = '';
          this.report.title = `${this.propertyName} - Rent Roll`;
        }

        const flatData = [];

        for (const prop of targetProperties) {
          let leasesQuery = query(collection(db, "Leases"), where("propertyId", "==", prop.id));
          if (this.unit?.unitName) {
            leasesQuery = query(leasesQuery, where("address", "==", this.unit.unitName));
          }
          const leasesSnapshot = await getDocs(leasesQuery);

          for (const leaseDoc of leasesSnapshot.docs) {
            const leaseData = leaseDoc.data();
            const leaseRef = leaseData.leaseReference;
            const tenantName = leaseData.tenantName || 'Unknown Tenant';
            const unit = leaseData.address?.includes("Unit") ? leaseData.address.split("Unit")[1].trim() : "";
            const txQuery = query(collection(db, "Transactions"), where("leaseReference", "==", leaseRef), orderBy("datePaid", "asc"));
            const txSnapshot = await getDocs(txQuery);
            const monthlyData = {};

            // === Transaction processing logic (unchanged) ===
            txSnapshot.docs.forEach(doc => {
              const t = doc.data();
              const date = new Date(t.datePaid);
              const monthYear = `${date.toLocaleString('default', { month: 'long' })} ${date.getFullYear()}`;
              if (!monthlyData[monthYear]) {
                monthlyData[monthYear] = {
                  openingBalance: 0,
                  rentCharged: 0, rentPaid: 0,
                  waterCharged: 0, waterChargedPaid: 0,
                  sewerageCharged: 0, sewerageChargedPaid: 0,
                  sewerageAv: 0, sewerageAvPaid: 0,
                  refuseCharged: 0, refuseChargedPaid: 0,
                  admin: 0, adminPaid: 0,
                  parking: 0, parkingPaid: 0,
                  insurance: 0, insurancePaid: 0,
                  legalExpenses: 0, legalExpensesPaid: 0,
                  repairsMaintenance: 0, repairsMaintenancePaid: 0,
                  utilities: 0, utilitiesPaid: 0,
                  otherVendorExpenses: 0, otherVendorExpensesPaid: 0,
                  otherCharge: 0, otherChargePaid: 0,
                  bankReceived: 0,
                  totalAllocated: 0,
                  unallocated: 0,
                  paymentDates: []
                };
              }
              const amount = parseFloat(t.amountPaid) || 0;
              const allocated = parseFloat(t.allocatedAmount) || 0;
              const desc = (t.description || t.transactionType || '').toLowerCase();

              if (t.type === "payment") {
                monthlyData[monthYear].bankReceived += Math.abs(amount);
                monthlyData[monthYear].paymentDates.push(t.datePaid);
              } else if (amount > 0) {
                if (/rent|monthly rent/i.test(desc)) {
                  monthlyData[monthYear].rentCharged += amount;
                  monthlyData[monthYear].rentPaid += allocated;
                } else if (/water/i.test(desc) && !/av|availability/i.test(desc)) {
                  monthlyData[monthYear].waterCharged += amount;
                  monthlyData[monthYear].waterChargedPaid += allocated;
                } else if (/sewerage/i.test(desc) && !/av|availability/i.test(desc)) {
                  monthlyData[monthYear].sewerageCharged += amount;
                  monthlyData[monthYear].sewerageChargedPaid += allocated;
                } else if (/sewerage?\s*av|sewerage?\s*availability/i.test(desc)) {
                  monthlyData[monthYear].sewerageAv += amount;
                  monthlyData[monthYear].sewerageAvPaid += allocated;
                } else if (/refuse/i.test(desc)) {
                  monthlyData[monthYear].refuseCharged += amount;
                  monthlyData[monthYear].refuseChargedPaid += allocated;
                } else if (/admin/i.test(desc)) {
                  monthlyData[monthYear].admin += amount;
                  monthlyData[monthYear].adminPaid += allocated;
                } else if (/parking/i.test(desc)) {
                  monthlyData[monthYear].parking += amount;
                  monthlyData[monthYear].parkingPaid += allocated;
                } else if (/insurance/i.test(desc)) {
                  monthlyData[monthYear].insurance += amount;
                  monthlyData[monthYear].insurancePaid += allocated;
                } else if (/legal/i.test(desc)) {
                  monthlyData[monthYear].legalExpenses += amount;
                  monthlyData[monthYear].legalExpensesPaid += allocated;
                } else if (/repair|maintenance/i.test(desc)) {
                  monthlyData[monthYear].repairsMaintenance += amount;
                  monthlyData[monthYear].repairsMaintenancePaid += allocated;
                } else if (/utilities/i.test(desc)) {
                  monthlyData[monthYear].utilities += amount;
                  monthlyData[monthYear].utilitiesPaid += allocated;
                } else if (/vendor/i.test(desc)) {
                  monthlyData[monthYear].otherVendorExpenses += amount;
                  monthlyData[monthYear].otherVendorExpensesPaid += allocated;
                } else {
                  monthlyData[monthYear].otherCharge += amount;
                  monthlyData[monthYear].otherChargePaid += allocated;
                }
                monthlyData[monthYear].totalAllocated += allocated;
              }
            });

            const sortedMonths = Object.keys(monthlyData).sort((a, b) => {
              const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
              const [ma, ya] = a.split(' '); const [mb, yb] = b.split(' ');
              return ya - yb || months.indexOf(ma) - months.indexOf(mb);
            });

            for (let i = 0; i < sortedMonths.length; i++) {
              const monthYear = sortedMonths[i];
              const m = monthlyData[monthYear];
              m.openingBalance = i === 0 ? 0 : (monthlyData[sortedMonths[i - 1]]?.closingBalance || 0);
              const currentCharges = m.rentCharged + m.waterCharged + m.sewerageCharged + m.sewerageAv + m.refuseCharged +
                m.admin + m.parking + m.insurance + m.legalExpenses + m.repairsMaintenance + m.utilities + m.otherVendorExpenses + m.otherCharge;
              m.subTotal = m.openingBalance + currentCharges;
              m.closingBalance = m.subTotal - m.bankReceived;
              m.unallocated = m.bankReceived - m.totalAllocated;

              const [monthName, year] = monthYear.split(' ');
              const monthStart = new Date(year, new Date(`${monthName} 1, ${year}`).getMonth(), 1);
              const monthEnd = new Date(year, monthStart.getMonth() + 1, 0);
              const rStart = new Date(this.report.parameters.startDate);
              const rEnd = new Date(this.report.parameters.endDate);

              if (monthStart <= rEnd && monthEnd >= rStart) {
                const row = {
                  month: monthYear,
                  reference: leaseRef,
                  tenantName: tenantName,
                  property: prop.displayName,
                  unit: unit,
                  bcForward: m.openingBalance,
                  rentCharged: m.rentCharged,
                  rentPaid: m.rentPaid,
                  waterCharged: m.waterCharged,
                  waterChargedPaid: m.waterChargedPaid,
                  sewerageCharged: m.sewerageCharged,
                  sewerageChargedPaid: m.sewerageChargedPaid,
                  sewerageAv: m.sewerageAv,
                  sewerageAvPaid: m.sewerageAvPaid,
                  refuseCharged: m.refuseCharged,
                  refuseChargedPaid: m.refuseChargedPaid,
                  admin: m.admin,
                  adminPaid: m.adminPaid,
                  parking: m.parking,
                  parkingPaid: m.parkingPaid,
                  insurance: m.insurance,
                  insurancePaid: m.insurancePaid,
                  legalExpenses: m.legalExpenses,
                  legalExpensesPaid: m.legalExpensesPaid,
                  repairsMaintenance: m.repairsMaintenance,
                  repairsMaintenancePaid: m.repairsMaintenancePaid,
                  utilities: m.utilities,
                  utilitiesPaid: m.utilitiesPaid,
                  otherVendorExpenses: m.otherVendorExpenses,
                  otherVendorExpensesPaid: m.otherVendorExpensesPaid,
                  otherCharge: m.otherCharge,
                  otherChargePaid: m.otherChargePaid,
                  subTotal: m.subTotal,
                  paymentDate: m.paymentDates.length > 0 ? m.paymentDates.map(d => new Date(d).toLocaleDateString('en-ZA')).join(', ') : '',
                  bankReceived: m.bankReceived,
                  totalAllocated: m.totalAllocated,
                  unallocated: m.unallocated,
                  balance: m.closingBalance
                };
                flatData.push(row);
              }
            }
          }
        }

        // Group by lease reference
        const grouped = [];
        const map = new Map();
        flatData.forEach(item => {
          const key = item.reference;
          if (!map.has(key)) {
            map.set(key, {
              reference: item.reference,
              tenantName: item.tenantName,
              property: item.property,
              unit: item.unit,
              items: []
            });
            grouped.push(map.get(key));
          }
          map.get(key).items.push(item);
        });

        this.groupedData = grouped;
        this.extractUniqueUnits();
        this.updateVisibleColumns();

      } catch (err) {
        console.error("Generate error:", err);
        this.groupedData = [];
      }
    },
    async generatePreview() {
      this.isGeneratingPreview = true;
      try {
        await this.generateRentRollData();
      } catch (error) {
        console.error(error);
      } finally {
        this.isGeneratingPreview = false;
      }
    },
    async generatePdf() {
      if (!this.displayedGroupedData.length) return;

      this.generatingPdf = true;
      await this.$nextTick();

      try {
        const pdf = new jsPDF({
          orientation: 'landscape',
          unit: 'pt',
          format: 'a4'
        });

        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 40;
        const usableWidth = pageWidth - 2 * margin;
        const headerReserve = 160;

        const headerEl = document.querySelector('#print-content .report-header');
        const headerCanvas = await html2canvas(headerEl, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
        const headerImg = headerCanvas.toDataURL('image/png');
        const headerHeight = Math.min((headerCanvas.height * usableWidth) / headerCanvas.width, headerReserve - 20);

        const addHeader = () => {
          pdf.addImage(headerImg, 'PNG', margin, margin, usableWidth, headerHeight);
        };

        let firstLease = true;

        for (let i = 0; i < this.displayedGroupedData.length; i++) {
          await this.$nextTick();
          const cardEl = document.querySelectorAll('#print-content .lease-card')[i];
          if (!cardEl) continue;

          if (!firstLease) pdf.addPage();
          firstLease = false;

          addHeader();

          const canvas = await html2canvas(cardEl, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff',
            logging: false
          });

          const imgData = canvas.toDataURL('image/png');
          const imgHeight = (canvas.height * usableWidth) / canvas.width;

          const availableHeight = pageHeight - margin - headerReserve - 20;
          let yPos = margin + headerReserve;

          if (imgHeight > availableHeight) {
            const scaleFactor = availableHeight / imgHeight;
            pdf.addImage(imgData, 'PNG', margin, yPos, usableWidth * scaleFactor, availableHeight);
          } else {
            pdf.addImage(imgData, 'PNG', margin, yPos, usableWidth, imgHeight);
          }
        }

        const legendEl = document.querySelector('#print-content .report-legend');
        if (legendEl) {
          pdf.addPage();
          addHeader();

          const legendCanvas = await html2canvas(legendEl, { scale: 2, useCORS: true, backgroundColor: '#ffffff' });
          const legendImg = legendCanvas.toDataURL('image/png');
          const legendHeight = (legendCanvas.height * usableWidth) / legendCanvas.width;

          pdf.addImage(legendImg, 'PNG', margin, margin + headerReserve, usableWidth, legendHeight);
        }

        const fileName = `${this.report.title.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().slice(0,10)}.pdf`;
        pdf.save(fileName);

      } catch (error) {
        console.error('PDF Error:', error);
      } finally {
        this.generatingPdf = false;
      }
    },
    formatCurrency(value) {
      if (value == null || value === '' || isNaN(value)) return '';
      return 'R ' + Number(value).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });
    }
  }
};
</script>

<style scoped>
.rent-roll-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem !important;
}
.rent-roll-table th,
.rent-roll-table td {
  border: 1px solid #e0e0e0;
  padding: 4px !important;
  text-align: left;
}
.rent-roll-table th {
  background-color: #f5f5f5;
  font-weight: bold;
}
.combined-cell {
  line-height: 1.4;
  font-size: 0.75rem;
}
.legend-square {
  width: 20px;
  height: 20px;
  border: 1px solid #ccc;
  display: inline-block;
}
.success-bg { background-color: #4CAF50; }
.orange-bg { background-color: #FF8F00; }
.error-bg { background-color: #F44336; }
.success--text { color: #4CAF50 !important; }
.orange--text { color: #FF8F00 !important; }
.error--text { color: #F44336 !important; }
.gap-2 > * + * { margin-left: 8px; }
.gap-4 > * + * { margin-left: 16px; }
.lease-card {
  background: white !important;
}

@media print {
  body * {
    visibility: hidden;
  }
  #print-content, #print-content * {
    visibility: visible;
  }
  #print-content {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>